import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import API from "../api/apiClient";


/* ===================== RESULT PAGE ===================== */
function Result() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const hasSavedRef = useRef(false);

  

  /* ===================== DATA FROM CAL.JSX ===================== */
  const rates = state?.rates;
const areaData = state?.areaData;
const base = state?.base;


  /* ===================== FLAGS ===================== */
  const isDDJAY = base.policy === "DDJAY Plotting";
  const isIndustrialPlotting = base.policy === "Industrial Plotting";
  const isTOD = base.policy === "TOD";

  /* ===================== HELPERS ===================== */
  const SQM_TO_ACRE = 4046.86;
  const TO_LAKH = 100000;

  const rsSqmToLakhAcre = (rate) => (rate * SQM_TO_ACRE) / TO_LAKH;

  const calcNormal = (area, rate) =>
    area && rate ? area * rate : 0;

  const calcRsSqm = (area, rate, far) =>
    area && rate ? area * rsSqmToLakhAcre(rate) * far : 0;
  const calcRsSqmNoFar = (area, rate) =>
  area && rate ? area * rsSqmToLakhAcre(rate) : 0;


  /* ===================== ZONE & DDJAY ===================== */
  const getZoneCategory = (zone) => {
    if (!zone) return "";
    if (zone.includes("High-I") || zone.includes("High-II")) return "HIGH";
    if (zone.includes("Medium")) return "MEDIUM";
    if (zone.includes("Low-I") || zone.includes("Low-II")) return "LOW";
    return "";
  };

  const getDDJAYPercent = (charge, zone) => {
    if (charge === "edc") {
      if (zone === "HIGH") return 25;
      if (zone === "MEDIUM") return 50;
      if (zone === "LOW") return 75;
    }
    if (
      ["idc", "conversion", "license", "scrutiny"].includes(charge) &&
      zone === "HIGH"
    ) {
      return 25;
    }
    return 0;
  };

  const zoneCategory = getZoneCategory(base.potentialZone);

  /* ===================== CALCULATIONS ===================== */
  const sections = ["residential", "commercial"];

  const result = sections.map((section) => {
    const area = Number(areaData[section].area);
    const far = Number(areaData[section].far); // ONLY from Cal.jsx

    /* -------- RAW -------- */
    const edcRaw = calcNormal(area, rates[section]?.edc);
    const licenseRaw = calcNormal(area, rates[section]?.license);
    const idw = calcNormal(area, rates[section]?.idw);

    const idc = calcRsSqm(area, rates[section]?.idc, far);
    const scrutiny = calcRsSqmNoFar(
  area,
  rates[section]?.scrutiny
);

    const conversionRaw = calcRsSqmNoFar(
  area,
  rates[section]?.conversion
);


    /* -------- TOD ADJUSTMENT -------- */
    const edc = isTOD ? edcRaw * (far / 1.75) : edcRaw;
    const license = isTOD ? licenseRaw * (far / 1.75) : licenseRaw;
    const conversion = isTOD
  ? conversionRaw * (far / 1.75)
  : conversionRaw;


    /* -------- IAC (₹/sqm, TOD ONLY, NO FAR) -------- */
    // IAC: ₹/sqm, NO FAR, TOD ONLY
const iac =
  isTOD && rates[section]?.iac
    ? area * rsSqmToLakhAcre(rates[section].iac)
    : 0;


    const total =
      edc + license + idw + idc + iac + conversion + scrutiny;

    return {
      section,
      edc,
      license,
      idw,
      idc,
      iac,
      conversion,
      scrutiny,
      total,
    };
  });

  const sumByKey = (key) =>
    result.reduce((sum, r) => sum + (r[key] || 0), 0);

  const grandTotal = sumByKey("total");

  /* ===================== DDJAY REBATE ===================== */
  const ddjayRebate = isDDJAY
    ? {
        edc:
          (sumByKey("edc") *
            getDDJAYPercent("edc", zoneCategory)) / 100,

        idc:
          (sumByKey("idc") *
            getDDJAYPercent("idc", zoneCategory)) / 100,

        license:
          (sumByKey("license") *
            getDDJAYPercent("license", zoneCategory)) / 100,

        conversion:
          (sumByKey("conversion") *
            getDDJAYPercent("conversion", zoneCategory)) / 100,

        scrutiny:
          (sumByKey("scrutiny") *
            getDDJAYPercent("scrutiny", zoneCategory)) / 100,
      }
    : {};

  const totalDDJAYRebate = isDDJAY
    ? Object.values(ddjayRebate).reduce((a, b) => a + b, 0)
    : 0;

  /* ===================== INDUSTRIAL REBATE ===================== */
  const residentialEDC =
    result.find((r) => r.section === "residential")?.edc || 0;

  const industrialRebate = isIndustrialPlotting
    ? residentialEDC * 0.5
    : 0;

  /* ===================== NET PAYABLE ===================== */
  const netPayable =
    grandTotal - totalDDJAYRebate - industrialRebate;

  /* ===================== UI ===================== */
  const chargeKeys = isTOD
    ? ["edc", "license", "idw", "idc", "iac", "conversion", "scrutiny"]
    : ["edc", "license", "idw", "idc", "conversion", "scrutiny"];

  const totalCharges = {};

chargeKeys.forEach((key) => {
  totalCharges[key] = sumByKey(key);
});


useEffect(() => {
  const calculationHash = JSON.stringify({
  district: base.district,
  location: base.location,
  urbanEstate: base.urbanEstate,
  potentialZone: base.potentialZone,
  policy: base.policy,
  areaData,
});

  const payload = {
  calculationHash,
  base,
  areaData,
  rates,
  totalCharges,
  ddjayRebate,
  industrialRebate,
  grandTotal,
  totalDDJAYRebate,
  netPayable,
};


  if (!state) return;
  if (hasSavedRef.current) return;

  hasSavedRef.current = true;

  API.post("/summary",  payload,
  {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`
    }
  })
    .then(res => {
    })
    .catch(err => {
      console.error("SAVE ERROR ❌", err.response || err);
    });
}, 
// eslint-disable-next-line
[state]);


/* ===================== GUARD ===================== */
  if (!state || !base || !areaData || !rates) {
  return (
    <div className="min-h-screen flex items-center justify-center text-red-600 font-semibold">
      No data received
    </div>
  );
}


  return (
    <div className="min-h-screen bg-slate-100 p-6">
      {/* HEADER */}
      <div className="max-w-7xl mx-auto bg-white shadow rounded-xl p-6 mb-6">
        <h2 className="text-2xl font-bold">Final Charges Summary</h2>
        <p className="text-sm text-slate-500">
          {base.urbanEstate} | {base.potentialZone} | {base.policy}
        </p>
      </div>

      {/* MAIN TABLE */}
      <div className="max-w-7xl mx-auto bg-white shadow rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-emerald-600 text-white">
            <tr>
              <th className="p-3 text-left">Charge</th>
              <th className="p-3">Residential / Industrial</th>
              <th className="p-3">Commercial</th>
              <th className="p-3">Total</th>
            </tr>
          </thead>
          <tbody>
            {chargeKeys.map((key) => (
              <tr key={key} className="border-b">
                <td className="p-3 capitalize font-medium">{key}</td>
                {result.map((r) => (
                  <td key={r.section} className="p-3 text-center">
                    ₹ {r[key].toFixed(2)}
                  </td>
                ))}
                <td className="p-3 text-center font-semibold">
                  ₹ {sumByKey(key).toFixed(2)}
                </td>
              </tr>
            ))}
            <tr className="bg-emerald-50 font-bold">
              <td className="p-3">Grand Total</td>
              {result.map((r) => (
                <td key={r.section} className="p-3 text-center">
                  ₹ {r.total.toFixed(2)}
                </td>
              ))}
              <td className="p-3 text-center">
                ₹ {grandTotal.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* DDJAY REBATE */}
      {isDDJAY && (
        <div className="max-w-7xl mx-auto mt-8 bg-white shadow rounded-xl p-6 border-l-8 border-emerald-600">
          <h3 className="text-xl font-semibold text-emerald-700 mb-4">
            DDJAY Policy Rebate
          </h3>

          <table className="w-full text-sm border">
            <thead className="bg-emerald-600 text-white">
              <tr>
                <th className="p-2 text-left">Charge</th>
                <th className="p-2 text-center">Rebate %</th>
                <th className="p-2 text-center">Rebate (₹ Lakh)</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(ddjayRebate).map((key) => (
                <tr key={key} className="border-b">
                  <td className="p-2 capitalize">{key}</td>
                  <td className="p-2 text-center">
                    {getDDJAYPercent(key, zoneCategory)}%
                  </td>
                  <td className="p-2 text-center font-semibold text-emerald-700">
                    ₹ {ddjayRebate[key].toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-emerald-50 font-semibold">
                <td className="p-2">Total Rebate</td>
                <td></td>
                <td className="p-2 text-center">
                  ₹ {totalDDJAYRebate.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* INDUSTRIAL REBATE */}
      {isIndustrialPlotting && (
        <div className="max-w-7xl mx-auto mt-8 bg-white shadow rounded-xl p-6 border-l-8 border-orange-500">
          <h3 className="text-xl font-semibold text-orange-600">
            Industrial Plotting Rebate
          </h3>
          <p className="text-sm text-gray-600">
            50% rebate on Residential/Industrial EDC
          </p>
          <div className="flex justify-between font-semibold text-orange-600 mt-2">
            <span>Rebate Amount</span>
            <span>₹ {industrialRebate.toFixed(2)} Lakh</span>
          </div>
        </div>
      )}

      {/* NET PAYABLE */}
      <div className="max-w-7xl mx-auto mt-8 bg-slate-900 text-white rounded-xl p-6 flex justify-between text-xl font-bold">
        <span>Net Payable Amount</span>
        <span>₹ {netPayable.toFixed(2)} Lakh</span>
      </div>

      {/* ACTIONS */}
      <div className="max-w-7xl mx-auto mt-6 flex justify-between">
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 rounded-lg bg-slate-300"
        >
          Back
        </button>
        <Link
          to="/cal"
          className="px-6 py-2 rounded-lg bg-emerald-600 text-white"
        >
          Done
        </Link>
      </div>
    </div>
  );
}

export default Result;
